// DepositQr — the deposit QR, drawn. What it says and which modules it has is
// depositQrModules.ts (MONEY PATH, read back by a decoder in depositQrModules.test.ts); this
// is only the drawing: the modules as soft runs, the three finder patterns as
// rounded squares, our mark in the hole. Dark ink on white.
//
// THE QUIET ZONE is in the drawing itself: QUIET modules of white on every
// side, in the symbol's own units, so the margin is exactly that many modules
// for every address (operator, 2026-10-18: "the QR's border is so big - I
// want it as by the standard"). Four modules: the quiet zone ISO/IEC 18004
// sets for a QR Code symbol (round 7; round 6's two were thinner than the
// standard). The read-back tests decode it with the card's dark ground right
// outside the white (ExchangeModal.qr.test.tsx, depositQrModules.test.ts). The
// plate around it adds no white of its own.
//
// THE MARK (round 9 — operator: "the black circle in the middle is crap ...
// prettier, maybe with our logo"; then: "the QR's logo is barely seen — at
// the top level, as the top companies have it"). The receive screens of the
// big wallets put their app's icon in the middle: a solid tile of the brand
// with the mark on it in white, a ring of clean white round it. So here: the
// hole, white (the tile the read-back takes, data-mark), and in it, a module
// in from every side of the hole, a rounded tile of the wils.trade gradient
// (GustMark's) with the Gust in white on it — the one coloured thing in the
// drawing, all of it inside the nine-module hole the code leaves clear. The
// symbol itself, every module and finder, stays ink on white. The hole is the
// same nine modules as before (depositQrModules.test.ts reads it back solid
// ink and two modules wider on every side).
//
// The box is 84 units a side whatever the symbol's size (the modules are
// scaled into it), so the plate lays it out the same for every address.
//
// It is given a text or it is not mounted: there is no "empty" QR, and the
// path is made from the text it was given on this render — nothing of a
// previous address can stay in it.

import { useId, useMemo } from 'react'
import { depositQr, depositQrPath } from './depositQrModules'

const BOX = 84
const INK = '#111113'
const PAPER = '#ffffff'
/** white modules round the symbol, on every side: ISO/IEC 18004's four */
export const QUIET = 4

// GustMark (ui/icons/GustMark.tsx, canonical wiki/design/logo/gust-gradient.svg):
// its box, its three strokes and its shear — not redrawn, only placed
const GUST_BOX = { x: -19, y: -30, w: 106, h: 112 } as const
const GUST_SHEAR = 'matrix(1 0 -0.12 1 6.24 0)'
const GUST_STROKES = [
  'M4 0H60A12 12 0 1 0 48 -12',
  'M-16 26H48',
  'M12 52H70A12 12 0 1 1 58 64',
] as const

// the Gust on the icon: its box set in the icon's 100 units, 58 high, in the
// middle (its own shear stays its own, after the placing)
const GUST_H = 58
const GUST_SCALE = GUST_H / GUST_BOX.h
const GUST_DX = (50 - (GUST_BOX.x + GUST_BOX.w / 2) * GUST_SCALE).toFixed(3)
const GUST_DY = (50 - (GUST_BOX.y + GUST_BOX.h / 2) * GUST_SCALE).toFixed(3)
const GUST_PLACE = `translate(${GUST_DX} ${GUST_DY}) scale(${GUST_SCALE.toFixed(4)}) ${GUST_SHEAR}`

export function DepositQr({ text, className }: { text: string; className?: string }) {
  const qr = useMemo(() => depositQr(text), [text])
  const path = useMemo(() => depositQrPath(qr), [qr])
  // one gradient per drawing: two QRs on a page must not share an id
  const gradient = `qr-gust-${useId().replace(/:/g, '')}`
  const k = BOX / (qr.size + QUIET * 2)
  const hole = qr.hole.to - qr.hole.from
  const centre = qr.hole.from + hole / 2
  // the white the read-back takes: the hole less half a module on every side
  const tile = hole - 1
  // the icon: the hole less a module of white on every side — the ring of
  // white between it and the nearest module (in the box's units, outside the
  // symbol's group: the read-back takes the group only)
  const icon = hole - 2

  return (
    <svg
      className={className}
      viewBox={`0 0 ${BOX} ${BOX}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      data-qr={qr.size}
      data-quiet={QUIET}
    >
      <g transform={`scale(${k}) translate(${QUIET} ${QUIET})`} data-part="symbol">
        <path d={path} fill={INK} />
        {qr.finders.map(([x, y]) => (
          <g key={`${x}-${y}`}>
            <rect x={x} y={y} width="7" height="7" rx="1.5" fill={INK} />
            <rect x={x + 1} y={y + 1} width="5" height="5" rx="0.9" fill={PAPER} />
            <rect x={x + 2} y={y + 2} width="3" height="3" rx="0.8" fill={INK} />
          </g>
        ))}
        <rect
          x={centre - tile / 2}
          y={centre - tile / 2}
          width={tile}
          height={tile}
          rx="2"
          fill={PAPER}
          data-mark={`${qr.hole.from} ${qr.hole.from} ${hole}`}
        />
      </g>
      {/* the icon in the hole: the gradient's tile, the Gust white on it.
          Its shapes stand straight in this svg, in no group: the read-back
          takes the symbol's group only. */}
      <svg
        x={(QUIET + centre - icon / 2) * k}
        y={(QUIET + centre - icon / 2) * k}
        width={icon * k}
        height={icon * k}
        viewBox="0 0 100 100"
        aria-hidden="true"
        data-part="qr-mark"
      >
        <defs>
          <linearGradient id={gradient} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#1F4CE0" />
            <stop offset="1" stopColor="#5A88FF" />
          </linearGradient>
        </defs>
        <rect width="100" height="100" rx="26" fill={`url(#${gradient})`} />
        {GUST_STROKES.map((d) => (
          <path
            key={d}
            d={d}
            transform={GUST_PLACE}
            fill="none"
            stroke={PAPER}
            strokeWidth="13"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>
    </svg>
  )
}
